// client.ts — per-session listener identity & color palette (docs §7.1).
//
// The web client generates a random `clientId` on first visit to a session and
// persists it in localStorage under key `daws:client:<sessionId>`. A different
// session id ⇒ a new clientId; reloading the same session ⇒ the same clientId
// (stable color + edit rights). Trust-based: edit/delete authorize on clientId
// match; the host (streamer JWT) overrides everything (docs §7.1).

/** localStorage key prefix for the per-session clientId (docs §7.1, verbatim). */
const CLIENT_KEY_PREFIX = "daws:client:";

/**
 * Fixed color palette (docs §7.1, verbatim & in order). The server assigns a
 * color by join order and returns it on join; this client-side copy is the
 * fallback used to render a color when the server has not (yet) supplied one
 * (e.g. a relayed comment whose `color` field is missing).
 */
export const COLOR_PALETTE: readonly string[] = [
  "#e6194b",
  "#3cb44b",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#42d4f4",
  "#f032e6",
  "#bfef45",
  "#fabed4",
  "#469990",
  "#dcbeff",
  "#9a6324",
];

/** localStorage key for a given session's clientId. */
export function clientKey(sessionId: string): string {
  return CLIENT_KEY_PREFIX + sessionId;
}

/** Generate a fresh random clientId (`cl_...`, docs §7 examples). */
export function newClientId(): string {
  // Prefer crypto.randomUUID where available; fall back to Math.random.
  const rnd =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return "cl_" + rnd;
}

/**
 * Read the persisted clientId for `sessionId`, creating and storing a new one
 * on first visit (docs §7.1). Resilient to localStorage being unavailable
 * (private mode / blocked storage): falls back to an in-memory id for the tab.
 */
export function loadOrCreateClientId(
  sessionId: string,
  storage: Storage | undefined = safeLocalStorage(),
): string {
  const key = clientKey(sessionId);
  if (storage) {
    try {
      const existing = storage.getItem(key);
      if (existing && existing.length > 0) return existing;
      const created = newClientId();
      storage.setItem(key, created);
      return created;
    } catch {
      /* fall through to ephemeral id */
    }
  }
  return newClientId();
}

/**
 * Deterministic palette color for a clientId, for the rare case the server
 * omits a `color`. Hash the id into a palette index so the same id always maps
 * to the same color within this client (stable, no flicker on re-render).
 */
export function colorForClientId(clientId: string | undefined): string {
  if (!clientId) return COLOR_PALETTE[0] as string;
  let h = 0;
  for (let i = 0; i < clientId.length; i++) {
    h = (h * 31 + clientId.charCodeAt(i)) >>> 0;
  }
  const idx = h % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx] as string;
}

/** Best-effort localStorage access (undefined when unavailable). */
function safeLocalStorage(): Storage | undefined {
  try {
    return typeof localStorage !== "undefined" ? localStorage : undefined;
  } catch {
    return undefined;
  }
}
